import { useState, useEffect } from "react";
import { Copy, Plus, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { supabase } from "@/lib/supabase";
import { format } from "date-fns";
import { captureError } from '@/lib/sentry';

interface ApiKey {
  id: string;
  api_key: string;
  created_at: string;
  revoked: boolean;
  revoked_at: string | null;
}

export function ApiKeySettings() {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newApiKey, setNewApiKey] = useState<string | null>(null);
  const { currentWorkspace } = useWorkspaces();
  const { toast } = useToast();

  const fetchApiKeys = async () => {
    if (!currentWorkspace) return;

    try {
      const { data, error } = await supabase
        .from('api_keys')
        .select('*')
        .eq('workspace_id', currentWorkspace.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setApiKeys(data || []);
    } catch (error) {
      console.error('Error fetching API keys:', error);
      captureError(error as Error, { feature: 'settings-api-key-settings-fetch-api-keys' });
      toast({
        title: "Error",
        description: "Failed to load API keys",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentWorkspace) {
      fetchApiKeys();
    }
  }, [currentWorkspace?.id]);

  const createApiKey = async () => {
    if (!currentWorkspace) return;

    try {
      const { data, error } = await supabase
        .rpc('generate_api_key', {
          p_workspace_id: currentWorkspace.id
        });

      if (error) throw error;

      const { error: insertError } = await supabase
        .from('api_keys')
        .insert({
          workspace_id: currentWorkspace.id,
          api_key: data,
        });

      if (insertError) throw insertError;

      setNewApiKey(data);
      await fetchApiKeys();
    } catch (error) {
      console.error('Error creating API key:', error);
      captureError(error as Error, { feature: 'settings-api-key-settings-create-api-key' });
      toast({
        title: "Error",
        description: "Failed to create API key",
        variant: "destructive",
      });
    }
  };

  const updateKeyStatus = async (keyId: string, revoked: boolean) => {
    try {
      const { error } = await supabase
        .from('api_keys')
        .update({ 
          revoked,
          revoked_at: revoked ? new Date().toISOString() : null
        })
        .eq('id', keyId);

      if (error) throw error;

      toast({
        description: `API key ${revoked ? 'revoked' : 'activated'} successfully`,
      });

      await fetchApiKeys();
    } catch (error) {
      console.error('Error updating API key:', error);
      captureError(error as Error, { feature: 'settings-api-key-settings-update-key-status' });
      toast({
        title: "Error",
        description: "Failed to update API key",
        variant: "destructive",
      });
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        description: "API key copied to clipboard",
      });
    } catch (error) {
      captureError(error as Error, { feature: 'settings-api-key-settings-copy-to-clipboard' });
      toast({
        title: "Error",
        description: "Failed to copy API key",
        variant: "destructive",
      });
    }
  };

  if (!currentWorkspace) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No workspace selected
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">API Keys</h2>
        <p className="text-muted-foreground">
          Manage API keys for programmatic access to your workspace
        </p>
      </div>

      <div className="flex justify-end">
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create API Key
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-8">Loading API keys...</div>
      ) : apiKeys.length > 0 ? (
        <div className="space-y-4">
          {apiKeys.map((key) => (
            <div
              key={key.id}
              className="flex items-center justify-between p-4 rounded-lg border bg-card"
            >
              <div className="space-y-1">
                <div className="font-mono text-sm">
                  {key.api_key.slice(0, 8)}...{key.api_key.slice(-8)}
                </div>
                <div className="text-sm text-muted-foreground">
                  Created {format(new Date(key.created_at), 'PPp')}
                  {key.revoked && key.revoked_at && (
                    <span className="ml-2">
                      • Revoked {format(new Date(key.revoked_at), 'PPp')}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(key.api_key)}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy
                </Button>
                <Select
                  value={key.revoked ? "revoked" : "active"}
                  onValueChange={(value) => updateKeyStatus(key.id, value === "revoked")}
                >
                  <SelectTrigger className="w-[100px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="revoked">Revoked</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          No API keys yet. Create your first API key to get started.
        </div>
      )}

      <div className="rounded-lg border p-4 bg-muted/50">
        <div className="flex gap-2">
          <AlertTriangle className="h-5 w-5 text-yellow-500" />
          <div className="text-sm">
            <p className="font-medium">Security Notice</p>
            <p className="text-muted-foreground">
              API keys provide full access to your workspace. Keep them secure and never share them publicly.
              Revoke any keys that you suspect have been compromised.
            </p>
            <p className="text-muted-foreground mt-2">
              For more information on how to use the API, please refer to the{' '}
              <a href="/api/docs" target="_blank" rel="noopener noreferrer" className="underline">
                API Documentation
              </a>
              .
            </p>
            <p className="text-muted-foreground mt-1 text-xs">
              API Base URL: <code className="bg-muted px-1 py-0.5 rounded">https://api.usebuildit.com/functions/v1/api-v1</code>
            </p>
          </div>
        </div>
      </div>

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create API Key</DialogTitle>
            <DialogDescription>
              {newApiKey ? (
                "Copy your API key now. You won't be able to see it again!"
              ) : (
                "Create a new API key for programmatic access to your workspace."
              )}
            </DialogDescription>
          </DialogHeader>

          {newApiKey ? (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg font-mono break-all">
                {newApiKey}
              </div>
              <div className="flex justify-between">
                <Button
                  variant="outline"
                  onClick={() => copyToClipboard(newApiKey)}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy
                </Button>
                <Button 
                  onClick={() => {
                    setShowCreateDialog(false);
                    setNewApiKey(null);
                  }}
                >
                  Done
                </Button>
              </div>
            </div>
          ) : (
            <DialogFooter>
              <Button onClick={createApiKey}>
                Create API Key
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}